<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import AppIcon from '@/components/ui/AppIcon.vue'

const props = withDefaults(
  defineProps<{
    open: boolean
    title?: string
    size?: 'sm' | 'md' | 'lg' | 'xl'
    closeOnBackdrop?: boolean
    showClose?: boolean
    /** Sit above another open modal, such as the course form. */
    elevated?: boolean
  }>(),
  { size: 'md', closeOnBackdrop: true, showClose: false, elevated: false },
)

const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()

/** Open modals, oldest first. Escape closes only the top one. */
const modalStack: symbol[] = []
const modalId = Symbol('modal')

const panel = ref<HTMLElement | null>(null)
let previouslyFocused: HTMLElement | null = null
/** True only when the press started on the backdrop, not inside the dialog. */
let pressedOnBackdrop = false

function onBackdropPointerDown(event: PointerEvent) {
  pressedOnBackdrop = event.target === event.currentTarget
}

function onBackdropPointerUp(event: PointerEvent) {
  const releasedOnBackdrop = event.target === event.currentTarget
  if (props.closeOnBackdrop && pressedOnBackdrop && releasedOnBackdrop) {
    emit('close')
  }
  pressedOnBackdrop = false
}

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}

/**
 * Keeps Tab inside the dialog. Without this, keyboard users tab straight into
 * the page behind the overlay.
 */
function syncStack(open: boolean) {
  const index = modalStack.indexOf(modalId)
  if (open && index === -1) modalStack.push(modalId)
  if (!open && index !== -1) modalStack.splice(index, 1)
}

function onKeydown(event: KeyboardEvent) {
  if (!props.open) return

  if (event.key === 'Escape') {
    if (modalStack[modalStack.length - 1] !== modalId) return
    emit('close')
    return
  }

  if (event.key !== 'Tab' || !panel.value) return

  const focusable = panel.value.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
  )
  if (focusable.length === 0) return

  const first = focusable[0]!
  const last = focusable[focusable.length - 1]!

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

watch(
  () => props.open,
  async (open) => {
    syncStack(open)
    if (open) {
      previouslyFocused = document.activeElement as HTMLElement
      document.body.style.overflow = 'hidden'
      await new Promise((resolve) => requestAnimationFrame(resolve))
      panel.value?.querySelector<HTMLElement>('input, textarea, select, button')?.focus()
    } else {
      if (modalStack.length === 0) document.body.style.overflow = ''
      previouslyFocused?.focus()
    }
  },
)

onMounted(() => document.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => {
  syncStack(false)
  document.removeEventListener('keydown', onKeydown)
  if (modalStack.length === 0) document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition duration-100 ease-in"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 flex items-end justify-center overflow-y-auto bg-gray-900/50 p-4 backdrop-blur-sm sm:items-center"
        :class="elevated ? 'z-[70]' : 'z-50'"
        @pointerdown="onBackdropPointerDown"
        @pointerup="onBackdropPointerUp"
      >
        <div
          ref="panel"
          role="dialog"
          aria-modal="true"
          :aria-label="title"
          class="relative flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-t-xl bg-white shadow-xl transition-all duration-300 sm:rounded-xl dark:bg-neutral-800"
          :class="SIZES[size]"
        >
          <button
            v-if="showClose && !title && !$slots.header"
            type="button"
            class="absolute end-3 top-3 z-10 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-neutral-700"
            @click="emit('close')"
          >
            <span class="sr-only">{{ t('common.close') }}</span>
            <AppIcon name="x" size="sm" />
          </button>
          <div
            v-if="title || $slots.header"
            class="flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 px-5 py-4 dark:border-neutral-700"
          >
            <slot name="header">
              <h2 class="text-base font-semibold text-gray-900 dark:text-white">{{ title }}</h2>
            </slot>
            <button
              type="button"
              class="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-neutral-700"
              @click="emit('close')"
            >
              <span class="sr-only">{{ t('common.close') }}</span>
              <AppIcon name="x" size="sm" />
            </button>
          </div>

          <div class="max-h-[70vh] min-h-0 overflow-y-auto px-5 py-4">
            <slot />
          </div>

          <div
            v-if="$slots.footer"
            class="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-200 px-5 py-4 sm:flex-row sm:justify-end dark:border-neutral-700"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
